<template>
  <div class="ts-view">
    <div v-if="(store.loading && !store.overview) || (loading && !summary)" class="ts-state"><span class="mdi mdi-loading ts-state__spinner"></span></div>

    <div v-else-if="store.error || loadError" class="ts-state ts-state--message">
      <span class="mdi mdi-alert-circle-outline ts-state__icon"></span>
      <p>{{ store.error === 'PORTAL_DISABLED' ? 'El portal de inquilinos no está disponible por el momento.' : 'No se pudo cargar tu resumen.' }}</p>
      <button v-if="loadError" class="ts-btn" @click="load">Reintentar</button>
    </div>

    <template v-else-if="summary">
      <!-- Totales de todas las naves -->
      <section class="ts-summary">
        <div class="ts-summary__cell">
          <span class="ts-summary__label">Por pagar</span>
          <span class="ts-summary__value" :style="{ color: pendingTotal > 0.005 ? '#1e3a5f' : GRAY }">{{ money(pendingTotal, primary.currency) }}</span>
        </div>
        <div class="ts-summary__cell">
          <span class="ts-summary__label">Vencido</span>
          <span class="ts-summary__value" :style="{ color: primary.overdue > 0.005 ? '#ef4444' : GRAY }">{{ money(primary.overdue, primary.currency) }}</span>
        </div>
        <div class="ts-summary__cell">
          <span class="ts-summary__label">Por vencer</span>
          <span class="ts-summary__value" :style="{ color: primary.upcoming > 0.005 ? '#f59e0b' : GRAY }">{{ money(primary.upcoming, primary.currency) }}</span>
        </div>
      </section>

      <div class="ts-scroll">
        <div class="ts-column">
          <p class="ts-context" :style="{ color: contextColor }">{{ contextText }}</p>
          <p v-if="otherCurrencyText" class="ts-note">{{ otherCurrencyText }}</p>

          <h2 class="ts-label">Tus naves ({{ summary.contracts.length }})</h2>
          <ul class="ts-list">
            <li v-for="c in summary.contracts" :key="c.id" class="ts-card">
              <div class="ts-card__head">
                <span class="ts-card__name">{{ c.plotName }}</span>
                <span class="ts-status" :class="`ts-status--${c.status.toLowerCase()}`">{{ STATUS_LABEL[c.status] || c.status }}</span>
              </div>

              <p class="ts-card__line" :class="`ts-card__line--${stateOf(c)}`">{{ lineOf(c) }}</p>
              <p v-if="otherLine(c)" class="ts-card__extra">{{ otherLine(c) }}</p>

              <div class="ts-card__actions">
                <button type="button" class="ts-link" @click="open(c.id, '/inquilino')">
                  <span class="mdi mdi-file-document-outline"></span> Ver contrato
                </button>
                <button type="button" class="ts-link" @click="open(c.id, '/inquilino/pagos')">
                  <span class="mdi mdi-cash-multiple"></span> Ver pagos
                </button>
              </div>
            </li>
          </ul>

          <p class="ts-foot">Este resumen suma lo vigente de todas tus naves. El detalle de cargos, pagos y estados de cuenta se consulta nave por nave.</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useTenantPortalStore } from '@/services/tenantPortalStore'
import { getPortalSummary } from '@/services/tenantPortal'

const router = useRouter()
const { state: store, loadOverview, selectContract } = useTenantPortalStore()

const summary = ref(null)
const loading = ref(false)
const loadError = ref(false)

const GRAY = '#94a3b8'
const STATUS_LABEL = { ACTIVE: 'Vigente', PENDING: 'Por iniciar', ENDED: 'Terminado' }
const SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

function money(amount, currency) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN' }).format(amount || 0)
}
function formatDate(iso) {
  const d = new Date(iso)
  // Las fechas se guardan a medianoche UTC: se leen en UTC para no correrlas un día.
  return `${d.getUTCDate()} ${SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

// --- Totales ---
const EMPTY = { currency: 'MXN', overdue: 0, upcoming: 0 }
const primary = computed(() => summary.value?.totals.balances[0] || EMPTY)
const pendingTotal = computed(() => primary.value.overdue + primary.value.upcoming)
const contextText = computed(() => {
  const t = summary.value?.totals
  if (!t) return ''
  if (t.overdueContracts > 0) return t.overdueContracts === 1 ? 'Tienes saldo vencido en 1 nave' : `Tienes saldo vencido en ${t.overdueContracts} naves`
  if (t.nextDue) return `Próximo vencimiento: ${formatDate(t.nextDue.date)} · ${t.nextDue.plotName}`
  return 'Al corriente · Sin saldo pendiente en ninguna nave'
})
const contextColor = computed(() => {
  const t = summary.value?.totals
  if (!t) return GRAY
  return t.overdueContracts > 0 ? '#ef4444' : t.nextDue ? '#f59e0b' : '#10b981'
})
const otherCurrencyText = computed(() => {
  const others = (summary.value?.totals.balances || []).slice(1).filter((b) => b.overdue + b.upcoming > 0.005)
  if (!others.length) return ''
  return `Además tienes pendiente: ${others.map((b) => money(b.overdue + b.upcoming, b.currency)).join(' · ')}`
})

// --- Por nave ---
function mainBalance(c) {
  return c.balances[0] || EMPTY
}
const hasMainOverdue = (c) => mainBalance(c).overdue > 0.005
function stateOf(c) {
  if (hasMainOverdue(c)) return 'overdue'
  if (c.nextDue) return 'due'
  return c.overdueDays > 0 ? 'overdue' : 'ok'
}
function lineOf(c) {
  const b = mainBalance(c)
  if (hasMainOverdue(c)) {
    const days = `${c.overdueDays} ${c.overdueDays === 1 ? 'día' : 'días'}`
    const extra = b.upcoming > 0.005 ? ` · ${money(b.upcoming, b.currency)} por vencer` : ''
    return `Vencido ${money(b.overdue, b.currency)} (hace ${days})${extra}`
  }
  if (c.nextDue) return `Por pagar ${money(c.nextDue.amount, c.nextDue.currency)} · vence el ${formatDate(c.nextDue.date)}`
  // Solo queda saldo en otra moneda (se muestra en la línea de abajo).
  return c.overdueDays > 0 ? 'Sin saldo pendiente en pesos' : 'Al corriente'
}
// Si la nave tiene saldo en otra moneda, se avisa en una línea aparte.
function otherLine(c) {
  const others = c.balances.slice(1).filter((b) => b.overdue + b.upcoming > 0.005)
  if (!others.length) return ''
  return `Además: ${others.map((b) => `${money(b.overdue + b.upcoming, b.currency)}${b.overdue > 0.005 ? ' (vencido)' : ''}`).join(' · ')}`
}

// Entrar al detalle individual: se elige ese contrato y se abre la pantalla correspondiente.
function open(contractId, path) {
  selectContract(contractId)
  router.push(path)
}

async function load() {
  loading.value = true
  loadError.value = false
  try {
    summary.value = await getPortalSummary()
  } catch {
    loadError.value = true
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  try {
    const overview = await loadOverview()
    // Con un solo contrato no hay nada que consolidar: se va directo a su detalle.
    if (overview.contracts.length <= 1) {
      router.replace('/inquilino')
      return
    }
  } catch {
    return
  }
  load()
})
</script>

<style scoped>
.ts-view { flex: 1; min-height: 0; display: flex; flex-direction: column; background: #fff; }
.ts-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 40px 16px; color: #94a3b8; flex: 1; }
.ts-state--message { text-align: center; font: 400 13px Inter, sans-serif; }
.ts-state__icon { font-size: 32px; }
.ts-state__spinner { font-size: 28px; animation: ts-spin 1s linear infinite; }
@keyframes ts-spin { to { transform: rotate(360deg); } }
.ts-btn { height: 32px; padding: 0 14px; border: none; border-radius: 6px; background: #3b82f6; color: #fff; font: 500 13px Inter, sans-serif; cursor: pointer; }

.ts-summary { display: flex; align-items: stretch; min-height: 80px; flex-shrink: 0; background: #f0f4f8; border-bottom: 1px solid #e2e8f0; }
.ts-summary__cell { flex: 1; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 4px; padding: 10px 8px; border-left: 1px solid #e2e8f0; min-width: 0; }
.ts-summary__cell:first-child { border-left: none; }
.ts-summary__label { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.ts-summary__value { font: 700 20px var(--font-mono); white-space: nowrap; }

.ts-scroll { flex: 1; overflow-y: auto; padding: 16px 16px 32px; }
.ts-column { max-width: 720px; margin: 0 auto; display: flex; flex-direction: column; gap: 14px; }
.ts-context { margin: 0; text-align: center; font: 400 12px Inter, sans-serif; }
.ts-note { margin: -6px 0 0; text-align: center; font: 400 11px Inter, sans-serif; color: #94a3b8; }
.ts-label { margin: 6px 0 0; font: 500 11px Inter, sans-serif; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }

.ts-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.ts-card { border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; background: #fff; }
.ts-card__head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.ts-card__name { font: 600 15px Inter, sans-serif; color: #1e3a5f; }
.ts-status { font: 500 11px Inter, sans-serif; padding: 3px 10px; border-radius: 12px; white-space: nowrap; }
.ts-status--active { background: #dcfce7; color: #15803d; }
.ts-status--pending { background: #fef3c7; color: #b45309; }
.ts-status--ended { background: #f1f5f9; color: #64748b; }
.ts-card__line { margin: 8px 0 0; font: 500 13px Inter, sans-serif; }
.ts-card__line--overdue { color: #dc2626; }
.ts-card__line--due { color: #d97706; }
.ts-card__line--ok { color: #059669; }
.ts-card__extra { margin: 3px 0 0; font: 400 11px Inter, sans-serif; color: #94a3b8; }
.ts-card__actions { display: flex; gap: 16px; margin-top: 12px; padding-top: 10px; border-top: 1px solid #f1f5f9; }
.ts-link { display: inline-flex; align-items: center; gap: 6px; padding: 0; border: none; background: none; color: #3b82f6; font: 500 12px Inter, sans-serif; cursor: pointer; }
.ts-link:hover { text-decoration: underline; }

.ts-foot { margin: 4px 0 0; text-align: center; font: 400 11px/1.6 Inter, sans-serif; color: #94a3b8; }

@media (max-width: 768px) {
  .ts-summary__value { font-size: 15px; }
}
</style>